import { useCallback, useEffect, useState } from 'react';
import type { AdminCurrentUser } from '../../api/types.js';
import { createCustomer, getUsageSummary, updateUsageQuota, type KimiPlanStatus, type UsageAccount, type UsageSummary } from '../../api/operationsApi.js';
import '../../styles/operations.css';

export function UsageConsole({ user, onToast }: { user: AdminCurrentUser; onToast: (message: string, type: 'success' | 'error') => void }) {
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [customerOpen, setCustomerOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setSummary(await getUsageSummary()); } catch (error) { onToast(error instanceof Error ? error.message : '用量加载失败', 'error'); }
    finally { setLoading(false); }
  }, [onToast]);
  useEffect(() => { void load(); }, [load]);

  if (loading && !summary) return <main className="ops-page"><div className="ops-empty">正在加载用量...</div></main>;
  if (!summary) return <main className="ops-page"><div className="ops-empty">暂无用量数据</div></main>;

  return <main className="ops-page">
    <header className="ops-header">
      <div><h1>{user.role === 'customer' ? 'AI 额度' : 'AI 用量与成本'}</h1><p>{summary.month.slice(0, 7)} · 统计客户消息触发的私聊与群聊 Sandbox 回复</p></div>
      <div className="ops-actions"><button type="button" onClick={() => void load()}>刷新</button>{user.role === 'admin' ? <button type="button" onClick={() => setCustomerOpen((value) => !value)}>客户账号</button> : null}</div>
    </header>
    <section className="ops-stats">
      <Stat label="已用额度" value={summary.totals.usedUnits} />
      <Stat label="剩余额度" value={summary.totals.remainingUnits} />
      <Stat label="总额度" value={summary.totals.quotaUnits} />
      {user.role === 'admin' ? <Stat label="Sandbox 创建" value={summary.totals.sandboxCreated} /> : null}
      {user.role === 'admin' ? <Stat label="预估成本" value={summary.costConfigured ? `¥${summary.totals.estimatedCostCny ?? 0}` : '未配置'} /> : null}
    </section>
    {user.role === 'admin' ? <KimiPlanTable plans={summary.kimiPlans} /> : null}
    {customerOpen && user.role === 'admin' ? <CustomerForm accounts={summary.accounts} onCreated={() => { setCustomerOpen(false); onToast('客户账号已创建', 'success'); }} /> : null}
    <section className="ops-table-wrap">
      <table className="ops-table"><thead><tr><th>工作号</th><th>进度</th><th>已用</th><th>剩余</th><th>月额度</th>{user.role === 'admin' ? <><th>Sandbox</th><th>成本</th></> : null}</tr></thead>
        <tbody>{summary.accounts.map((account) => <tr key={account.wechatRecordId}>
          <td><strong>{account.nickname || account.wechatAlias || account.wechatId}</strong><small title={account.wechatId}>{usageAccountMeta(account)}</small></td>
          <td><div className="quota-track"><span style={{ width: `${account.usagePercent}%` }} /></div><small>{account.usagePercent}%</small></td>
          <td>{account.usedUnits}</td><td>{account.remainingUnits}</td>
          <td>{user.role === 'admin' ? <QuotaEditor value={account.quotaUnits} onSave={async (value) => { await updateUsageQuota(account.wechatRecordId, value); await load(); }} /> : account.quotaUnits}</td>
          {user.role === 'admin' ? <><td>{account.sandboxCreated ?? 0}</td><td>{summary.costConfigured ? `¥${account.estimatedCostCny ?? 0}` : '未配置'}</td></> : null}
        </tr>)}</tbody></table>
    </section>
  </main>;
}

function KimiPlanTable({ plans }: { plans: KimiPlanStatus[] }) {
  return <section className="ops-plan-section">
    <div className="ops-section-title"><h2>Kimi 账号额度</h2><span>额度快照由 121 监控器更新</span></div>
    <div className="ops-table-wrap">
      <table className="ops-table ops-plan-table"><thead><tr><th>账号</th><th>状态</th><th>5 小时剩余</th><th>周剩余</th><th>并发</th><th>快照时间</th></tr></thead>
        <tbody>{plans.length > 0 ? plans.map((plan) => <tr key={plan.planId}>
          <td><strong>{plan.name}</strong><small>{plan.owner || plan.planId}</small></td>
          <td><span className={`plan-state ${plan.state}`}>{planStateLabel(plan.state)}</span></td>
          <td>{plan.snapshot ? <QuotaWindow window={plan.snapshot.fiveHour} /> : '-'}</td>
          <td>{plan.snapshot ? <QuotaWindow window={plan.snapshot.weekly} /> : '-'}</td>
          <td>{plan.activeLeases} / {plan.capacity}</td>
          <td>{plan.snapshot ? formatDateTime(plan.snapshot.observedAt) : '-'}</td>
        </tr>) : <tr><td colSpan={6}>暂无 Kimi 账号快照</td></tr>}</tbody>
      </table>
    </div>
  </section>;
}

function QuotaWindow({ window }: { window: { used: number; limit: number; resetAt: string } }) {
  const remaining = Math.max(0, Math.round((1 - window.used / window.limit) * 100));
  return <><strong>{remaining}%</strong><small>{formatDateTime(window.resetAt)} 重置</small></>;
}

function planStateLabel(state: KimiPlanStatus['state']): string {
  return ({ healthy: '可用', missing: '无快照', stale: '已过期', cooldown: '冷却中', low_quota: '额度不足', saturated: '并发已满' })[state];
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-CN', { hour12: false });
}

function usageAccountMeta(account: UsageAccount): string {
  const identity = account.wechatAlias || (account.nickname ? account.wechatId : null);
  return [identity, `#${account.wechatRecordId}`].filter(Boolean).join(' · ');
}

function Stat({ label, value }: { label: string; value: string | number }) { return <div className="ops-stat"><span>{label}</span><strong>{value}</strong></div>; }

function QuotaEditor({ value, onSave }: { value: number; onSave: (value: number) => Promise<void> }) {
  const [draft, setDraft] = useState(String(value));
  return <div className="quota-editor"><input aria-label="月额度" inputMode="numeric" value={draft} onChange={(event) => setDraft(event.target.value)} /><button type="button" onClick={() => void onSave(Number(draft))}>保存</button></div>;
}

function CustomerForm({ accounts, onCreated }: { accounts: UsageSummary['accounts']; onCreated: () => void }) {
  const [username, setUsername] = useState(''); const [password, setPassword] = useState(''); const [selected, setSelected] = useState<number[]>([]);
  return <form className="ops-form" onSubmit={(event) => { event.preventDefault(); void createCustomer({ username, password, wechatRecordIds: selected }).then(onCreated); }}>
    <label>登录名<input required value={username} onChange={(event) => setUsername(event.target.value)} /></label>
    <label>初始密码<input required minLength={8} type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
    <fieldset><legend>可见工作号</legend>{accounts.map((account) => <label key={account.wechatRecordId}><input type="checkbox" checked={selected.includes(account.wechatRecordId)} onChange={(event) => setSelected((ids) => event.target.checked ? [...ids, account.wechatRecordId] : ids.filter((id) => id !== account.wechatRecordId))} />{account.nickname || account.wechatId}</label>)}</fieldset>
    <button type="submit" disabled={!username || password.length < 8 || selected.length === 0}>创建</button>
  </form>;
}
